import React, { useState, useEffect, useCallback } from 'react';
import { vendutoApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Gauge, PieChart, Pizza, TrendingUp, Trophy } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import VendutoHeader from '../components/venduto/VendutoHeader';
import PeriodPicker from '../components/venduto/PeriodPicker';
import GiornoServizioCard from '../components/venduto/GiornoServizioCard';
import { useVendutoPeriodo } from '../hooks/useVendutoPeriodo';
import {
  periodoRange, fmtEuro, fmtNum, fmtPct, SERVIZI, SERVIZI_FILTRO, MACRO_COLORS,
} from '../lib/venduto';

const METRICHE_ANDAMENTO = [
  { key: 'coperti_medi', label: 'Coperti medi', fmt: (v) => fmtNum(v, 1) },
  { key: 'riempimento', label: 'Riempimento', fmt: (v) => fmtPct(v) },
  { key: 'incasso', label: 'Incasso', fmt: (v) => fmtEuro(v) },
];

const Barra = ({ percentuale, colore, testid }) => (
  <div className="h-2 rounded-full bg-muted overflow-hidden" data-testid={testid}>
    <div
      className="h-full rounded-full transition-all duration-500"
      style={{ width: `${Math.min(100, (percentuale || 0) * 100)}%`, backgroundColor: colore }}
    />
  </div>
);

const VendutoAnalisiPage = () => {
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useVendutoPeriodo(stato);
  const [analisi, setAnalisi] = useState(null);
  const [classifiche, setClassifiche] = useState([]);
  const [servizioClassifica, setServizioClassifica] = useState('tutti');
  const [metricaAndamento, setMetricaAndamento] = useState('coperti_medi');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendutoApi.getStato().then(setStato).catch(() => setLoading(false));
  }, []);

  const carica = useCallback(async () => {
    if (!sel || !stato) return;
    setLoading(true);
    try {
      const r = periodoRange(sel, stato);
      const [a, c] = await Promise.all([
        vendutoApi.getAnalisi(r.data_inizio, r.data_fine),
        vendutoApi.getClassifiche(r.data_inizio, r.data_fine, servizioClassifica),
      ]);
      setAnalisi(a);
      setClassifiche(c);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel calcolo delle analisi');
    } finally {
      setLoading(false);
    }
  }, [sel, stato, servizioClassifica]);

  useEffect(() => { carica(); }, [carica]);

  const range = sel && stato ? periodoRange(sel, stato) : null;
  const metrica = METRICHE_ANDAMENTO.find((m) => m.key === metricaAndamento);
  const datiGrafico = (analisi?.andamento?.punti || []).map((p) => {
    const row = { label: p.label };
    SERVIZI.slice(0, 3).forEach((s) => {
      row[s.key] = p.servizi?.[s.key]?.[metricaAndamento] ?? null;
    });
    return row;
  });

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader titolo="Analisi" sottotitolo={range?.label} largo />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4 lg:max-w-[1600px] lg:px-6 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 lg:items-start 2xl:grid-cols-3">
        {stato && sel && (
          <Card className="lg:col-span-2 2xl:col-span-3"><CardContent className="p-4">
            <PeriodPicker value={sel} onChange={setSel} archivio={stato} />
          </CardContent></Card>
        )}

        {loading && <div className="flex justify-center py-10 lg:col-span-2 2xl:col-span-3"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>}

        {!loading && analisi && (
          <>
            {/* Riempimento sala */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-primary" /> Riempimento della sala
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="riempimento-card">
                {analisi.riempimento.map((r) => {
                  const colore = SERVIZI.find((s) => s.key === r.servizio)?.color || '#D94F3D';
                  return (
                    <div key={r.servizio} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{r.label}</span>
                        <span className="font-mono-numbers font-semibold" data-testid={`riempimento-${r.servizio}`}>
                          {fmtPct(r.percentuale)}
                        </span>
                      </div>
                      <Barra percentuale={r.percentuale} colore={colore} />
                      <p className="text-[11px] text-muted-foreground font-mono-numbers">
                        {fmtNum(r.coperti_medi, 1)} coperti medi su capienza {fmtNum(r.capienza_media, 0)} · {r.giornate} giornate
                      </p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Composizione incasso */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-primary" /> Composizione dell'incasso
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="composizione-card">
                {analisi.composizione.filter((c) => c.fatturato > 0).map((c) => (
                  <div key={c.servizio} className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <p className="text-sm font-semibold">{c.label}</p>
                      <p className="text-xs font-mono-numbers text-muted-foreground">{fmtEuro(c.fatturato)}</p>
                    </div>
                    {c.voci.map((v) => (
                      <div key={v.macro} className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">{v.macro}</span>
                          <span className="font-mono-numbers">
                            {fmtPct(v.percentuale)} · {fmtEuro(v.fatturato)}
                          </span>
                        </div>
                        <Barra percentuale={v.percentuale} colore={MACRO_COLORS[v.macro] || '#A8A29E'} />
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Focus Pizza Bruna */}
            <Card className="border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Pizza className="w-4 h-4 text-primary" /> Pizza Bruna alla cena
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3 text-center" data-testid="pizza-bruna-card">
                <div>
                  <p className="text-xl font-bold font-mono-numbers text-primary" data-testid="pizza-quota">
                    {fmtPct(analisi.pizza_bruna.quota)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Quota incasso serale</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers">{fmtNum(analisi.pizza_bruna.pizze)}</p>
                  <p className="text-[11px] text-muted-foreground">Pizze servite</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers">{fmtNum(analisi.pizza_bruna.pizze_per_coperto, 2)}</p>
                  <p className="text-[11px] text-muted-foreground">Pizze per coperto</p>
                </div>
                <div className="col-span-3 text-[11px] text-muted-foreground font-mono-numbers">
                  {fmtEuro(analisi.pizza_bruna.fatturato_pizza)} su {fmtEuro(analisi.pizza_bruna.fatturato_cena)} ·
                  {' '}{fmtNum(analisi.pizza_bruna.coperti_cena)} coperti a cena
                </div>
              </CardContent>
            </Card>

            {/* Fatturato, coperti e coperto medio per giorno e servizio */}
            <GiornoServizioCard dati={analisi.giorno_servizio} className="lg:col-span-2 2xl:col-span-3" />

            {/* Andamento nel tempo */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> Andamento nel tempo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="andamento-card">
                <Select value={metricaAndamento} onValueChange={setMetricaAndamento}>
                  <SelectTrigger className="h-10 w-[180px]" data-testid="andamento-metrica">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METRICHE_ANDAMENTO.map((m) => (
                      <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Un punto per {analisi.andamento.bucket === 'giorno' ? 'giorno' : 'mese'} · una linea per servizio
                </p>
                <div className="h-56 lg:h-80 -ml-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={datiGrafico}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} width={45} />
                      <Tooltip formatter={(v) => metrica.fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {SERVIZI.slice(0, 3).map((s) => (
                        <Line
                          key={s.key}
                          type="monotone"
                          dataKey={s.key}
                          name={s.short}
                          stroke={s.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Classifiche per categoria */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-primary" /> Top 10 per categoria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="classifiche-card">
                <Select value={servizioClassifica} onValueChange={setServizioClassifica}>
                  <SelectTrigger className="h-10 w-[190px]" data-testid="classifiche-servizio">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVIZI_FILTRO.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {classifiche.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Nessun prodotto nel periodo/servizio scelto.</p>
                )}

                {classifiche.map((c) => (
                  <div key={c.categoria} className="space-y-1" data-testid={`classifica-${c.categoria}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{c.label}</p>
                      <Badge variant="secondary" className="font-mono-numbers text-[10px]">
                        {fmtEuro(c.fatturato_totale)}
                      </Badge>
                    </div>
                    <div className="divide-y divide-border/60">
                      {c.prodotti.map((p, i) => (
                        <div key={p.prodotto} className="flex items-center justify-between py-1.5 text-xs">
                          <span className="truncate pr-2">
                            <span className="text-muted-foreground font-mono-numbers mr-2">{i + 1}.</span>
                            {p.prodotto}
                          </span>
                          <span className="font-mono-numbers shrink-0">
                            {fmtNum(p.quantita, 0)} pz · {fmtEuro(p.fatturato)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default VendutoAnalisiPage;
